import { useCallback, useEffect, useMemo, useRef } from "react";

import { useNode } from "@craftjs/core";
import _, { get, isEmpty as isEmptyLodash, isEqual, set, cloneDeep, isPlainObject, startCase, round, omit, debounce } from "lodash";

import { usePropsIgnoreDevice, useResponsive, useUtilities } from "@/modules/cms/views/post/components/builder/context";
import { useHistory } from "@/modules/cms/views/post/components/builder/hooks/useHistory";

import { useStylePatternUpdater } from "../properties/StylePattern/hooks";
import { TRANSFORM_CONFIG } from "../properties/Transform";
import { getGradientColorValue } from "../properties/components/gradient/Preview";

import { useBlockDefinition } from "./definition";
import { UNIT_OPTIONS, UTILITIES } from "./predefined_sizes";
import { useActiveSharedPattern, useStylePatterns } from "./tailwindConfig/hooks";

import { SHARED_KEYS, SHARED_PATTERN_DEFS } from ".";

const HTML_BREAKPOINT = 'lg:'

let globalUtilitiesList = UTILITIES

function updateGlobalUtilities(customUtilities) {
  globalUtilitiesList = [
    ...UTILITIES.map(item => item.value),
    ...customUtilities,
  ]
}

export const isDeviceUtilityStructure = value => hasKey(value, 'default') || hasKey(value, 'mobile') || hasKey(value, 'desktop')

export const validValue = value => value && !isDeviceUtilityStructure(value)

const isUtilityOnly = value => {
  if (!isPlainObject(value) || isEmptyLodash(value)) return false

  const utilities = globalUtilitiesList.map(utility => utility.value ?? utility)
  return Object.keys(value).every(key => utilities.includes(key))
}

export function isInheriting({ props, keys, device, utilitiesParsed = '', patternData = {}, stylePattern = '' }) {
  const opositeDevice = device === 'desktop' ? 'mobile' : 'desktop'

  const getKeys = device => keys.map(key => [
    [key, device, utilitiesParsed].filter(Boolean).join('.'),
    device === 'desktop' ? [key, utilitiesParsed].filter(Boolean).join('.') : undefined,
  ]).flat().filter(Boolean)

  const getValue = scenarios => scenarios.map(key => {
    const value = get(props, key) || get(props, `${key}.default`)
    const rootKey = keys.find(item => key.startsWith(item))
    return validValue(value) ? rootKey : false
  }).filter(Boolean)

  const deviceKeys = getKeys(device)
  const opositeDeviceKeys = getKeys(opositeDevice)

  const deviceHasValue = getValue(deviceKeys)
  const opositeDeviceHasValue = getValue(opositeDeviceKeys)

  const inheritingBothDevices = [...new Set(
    opositeDeviceHasValue.filter(item => !deviceHasValue.includes(item)))]
    .map(item => startCase(item.replaceAll('.', ' ')
      .replace('mobile', '')
      .replace('default', '')
      .trim()))

  const getPatternValue = scenarios => scenarios.map(key => {
    const value = get(patternData, key) || get(patternData, `${key}.default`)
    const rootKey = keys.find(item => key.startsWith(item))
    return validValue(value) ? rootKey : false
  }).filter(Boolean)

  const patternDeviceHasValue = stylePattern && patternData ? getPatternValue(deviceKeys) : []

  const inheritingFromPattern = stylePattern && patternDeviceHasValue.length && !deviceHasValue.length ? stylePattern : false

  return {
    inheriting: !deviceHasValue.length && !!opositeDeviceHasValue.length,
    deviceHasValue: !!deviceHasValue.length,
    inheritingBothDevices,
    inheritingFromPattern,
  }
}

export function propHasAnyValue(obj, keys = []) {
  if (obj == null || !Array.isArray(keys) || keys.length === 0) return false

  for (const key of keys) {
    const value = get(obj, key)
    if (hasAnyValue(value)) return true
  }

  return false
}

function hasAnyValue(value) {
  if (value == null) return false

  if (typeof value === 'object') {
    for (const key in value) {
      if (hasAnyValue(value[key])) return true
    }
    return false
  }

  return true
}

export function useCustomProp(name, defaultValue) {
  const { ignore } = useHistory()
  const { id, actions: { setCustom }, custom } = useNode(({ data: { custom } }) => ({ custom }))
  const value = get(custom, name)

  const setIgnored = useMemo(() => ignore.current().setCustom, [ignore])

  const parse = useCallback(({ props, name, value }) => {
    set(props, name, value)
  }, [])

  const setValue = useCallback(value => {
    setCustom(props => parse({ props, name, value }))
  }, [setCustom, parse, name])

  const setValueIgnoring = useCallback(value => {
    setIgnored(id, props => parse({ props, name, value }))
  }, [id, setIgnored, parse, name])

  useEffect(() => {
    if (defaultValue !== undefined) {
      setValueIgnoring(defaultValue)
    }
  }, [defaultValue, setValueIgnoring])

  return { value, setValue }
}

export function useCraftProp(name, defaultValue) {
  const { device } = useResponsive()
  const { selectedUtilities } = useUtilities()
  const { ignore } = useHistory()

  const { value: propsIgnoreDevice } = usePropsIgnoreDevice()
  const ignoreDevice = useMemo(() => name?.includes('attributes') || propsIgnoreDevice?.includes(name), [name, propsIgnoreDevice])
  const { id, actions: { setProp }, props, custom } = useNode(({ data: { props: { properties }, custom } }) => ({ props: properties, custom }));
  const { getDefinition } = useBlockDefinition()

  const { stylePatternAutoUpdate, stylePatternValue } = useMemo(() => ({
    stylePatternAutoUpdate: custom?.stylePatternAutoUpdate,
    stylePatternValue: props?.stylePattern,
  }), [custom, props])

  const { value: stylePattern } = useStylePatterns(stylePatternValue)

  const { update: updateStylePattern } = useStylePatternUpdater(stylePatternAutoUpdate, `${stylePatternValue}.${name}`)
  const rawValueMemo = useRef(undefined)

  const getProps = useCallback(() => {
    const definition = getDefinition(id)

    if (definition?.isShared) {
      return stylePattern
    }

    if (stylePatternValue) {
      return {
        ...stylePattern,
        ...props,
      }
    }

    return props
  }, [id, getDefinition, stylePattern, props, stylePatternValue])

  const currentRaw = get(getProps(), name)
  if (!isEqual(rawValueMemo.current, currentRaw)) {
    rawValueMemo.current = currentRaw
  }
  const rawValue = rawValueMemo.current

  const setIgnored = useCallback((nodeId, callback) => ignore.current().setProp(nodeId, callback), [])

  const value = useMemo(() => {
    if (ignoreDevice) {
      return rawValue
    }

    if (rawValue === undefined) {
      return undefined
    }

    const { mobile, desktop } = rawValue || {}

    const getUtilityValue = value => {
      if (!value) return value
      const withoutUtility = value?.default !== undefined ? value?.default : isUtilityOnly(value) ? undefined : value
      const defaultValue = get(value, [...selectedUtilities, 'default'])
      return !selectedUtilities.length ? withoutUtility : defaultValue !== undefined ? defaultValue : get(value, selectedUtilities)
    }

    const mobileUtility = getUtilityValue(mobile)
    const desktopUtility = getUtilityValue(desktop)
    const rawUtility = getUtilityValue(rawValue)

    const values = {
      mobile: mobileUtility !== undefined ? mobileUtility : desktopUtility || (hasKeyDeep(rawValue, 'mobile') ? undefined : rawUtility),
      desktop: desktopUtility !== undefined ? desktopUtility : mobileUtility || (hasKeyDeep(rawValue, 'mobile') ? undefined : rawUtility),
    }

    return values[device]
  }, [device, rawValue, ignoreDevice, selectedUtilities])

  const parse = useCallback(({ props, newValue, overlayDevice, overlayUtility }) => {
    if (ignoreDevice) {
      set(props, `properties.${name}`, newValue)
      return
    }

    const { mobile, desktop } = rawValue || {}

    const utility = overlayUtility || selectedUtilities
    const values = {
      raw: setUtility(rawValue, newValue, utility),
      desktop: { desktop: setUtility(desktop, newValue, utility), mobile },
      mobile: { mobile: setUtility(mobile, newValue, utility), desktop: desktop || (hasKeyDeep(rawValue, 'mobile') ? undefined : rawValue) },
    }

    const deviceToUse = overlayDevice || device
    const data = deviceToUse === 'desktop' && !mobile ? values.raw : values[deviceToUse]

    return set(props, `properties.${name}`, data === '' ? undefined : data)
  }, [name, device, ignoreDevice, rawValue, selectedUtilities])

  const setValue = useCallback((newValue = null, overlayDevice, overlayUtility) => {
    setProp(props => {
      parse({ props, newValue, overlayDevice, overlayUtility })
    })
    if (stylePatternAutoUpdate && stylePatternValue) debounce(() => updateStylePattern(name), 100)()
  }, [setProp, parse, stylePatternAutoUpdate, stylePatternValue, updateStylePattern, name])

  const setValueIgnoring = useCallback((newValue = null, overlayDevice) => {
    setIgnored(id, props => parse({ props, newValue, overlayDevice }))
  }, [id, setIgnored, parse])

  const initialized = useRef(false)
  useEffect(() => {
    if (!initialized.current) {
      if (value === undefined && defaultValue !== undefined) {
        setValueIgnoring(defaultValue)
      }
      initialized.current = true
    }
  }, [value, defaultValue, setValueIgnoring])

  useEffect(() => {
    if (defaultValue !== undefined && ignoreDevice) {
      setValueIgnoring(defaultValue)
    }
  }, [defaultValue, setValueIgnoring, ignoreDevice])

  if (!name) {
    return { value: '', setValue: () => { } }
  }

  return { value, rawValue, setValue }
}

export function usePureCraftProp(name, defaultValue) {
  const { ignore } = useHistory()
  const setIgnored = useMemo(() => ignore.current().setProp, [ignore])

  const { id, actions: { setProp }, value } = useNode(({ data: { props } }) => ({
    value: get(props, name),
  }))

  const parse = useCallback(({ props, name, value }) => {
    set(props, name, value)
  }, [])

  const setValue = useCallback(value => {
    setProp(props => parse({ props, name, value }))
  }, [setProp, parse, name])

  const setValueIgnoring = useCallback(value => {
    setIgnored(id, props => parse({ props, name, value }))
  }, [id, setIgnored, parse, name])

  useEffect(() => {
    if (defaultValue !== undefined) {
      setValueIgnoring(defaultValue)
    }
  }, [defaultValue, setValueIgnoring])

  return { value, setValue }
}

function useBuildStylePatternCSS() {
  const { value: patterns = {}, updateValue: updatePatterns } = useStylePatterns()

  useEffect(() => {
    const keysToProcess = SHARED_KEYS.filter(key => patterns[key])
    if (!keysToProcess.length) return

    const needsUpdate = keysToProcess.some(key => patterns[key]?.parsedClassName !== parseClassName(patterns[key]))
    if (!needsUpdate) return

    updatePatterns(currentPatterns => {
      const updated = {}
      SHARED_KEYS.forEach(key => {
        if (currentPatterns[key]) {
          updated[key] = { ...currentPatterns[key], parsedClassName: parseClassName(currentPatterns[key]) }
        }
      })
      return { ...currentPatterns, ...updated }
    })
  }, [patterns, updatePatterns])
}

function useSeedSharedStylePatterns() {
  const { value: patterns = {}, updateValue: updatePatterns } = useStylePatterns()
  const isActive = useActiveSharedPattern()

  useEffect(() => {
    const missing = Object.entries(SHARED_PATTERN_DEFS).reduce((acc, [pattern, { opts, definition }]) => {
      if (patterns[pattern] || !isActive(pattern)) return acc

      acc[pattern] = { ...definition?.defaultProps?.properties, opts }
      return acc
    }, {})

    if (!Object.keys(missing).length) return

    updatePatterns(current => ({ ...missing, ...current }))
  }, [patterns, updatePatterns, isActive])
}

export function useInitStylePattern() {
  useSeedSharedStylePatterns()
  useBuildStylePatternCSS()
}

export function setUtility(oldValue = null, newValue, selectedUtilities) {
  const oldValueHasUtilityKey = hasKeyDeep(oldValue, 'default')

  if (!selectedUtilities.length) {
    return oldValueHasUtilityKey ? { ...oldValue, default: newValue } : newValue
  }

  const createFirstUtility = !oldValueHasUtilityKey
  if (createFirstUtility) {
    if (selectedUtilities.length > 1) {
      const nested = selectedUtilities.reduceRight((acc, key, i) => i === selectedUtilities.length - 1 ? { [key]: newValue } : { [key]: { default: null, ...acc } }, {})
      return { default: oldValue, ...nested }
    }
    return { default: oldValue, [selectedUtilities[0]]: newValue }
  }

  const values = { value: get(oldValue, selectedUtilities), default: get(oldValue, [...selectedUtilities, 'default']) }
  const updateRaw = values.value !== undefined || values.default !== undefined
  if (updateRaw) {
    const key = values.default !== undefined ? [...selectedUtilities, 'default'] : selectedUtilities
    return set(cloneDeep(oldValue), key, newValue)
  }

  return setUtilityPath(oldValue, newValue, selectedUtilities)
}

const setUtilityPath = (oldValue, newValue, selectedUtilities) => {
  let response = cloneDeep(oldValue)

  for (let i = 0; i < selectedUtilities.length; i++) {
    const path = selectedUtilities.slice(0, i + 1)
    const pathDefault = [...path, 'default']
    const currentValue = get(response, path)
    const currentDefault = get(response, pathDefault)
    const isUtilityTarget = i === selectedUtilities.length - 1

    if (isUtilityTarget) {
      const hasExistingValue = currentValue !== undefined || currentDefault !== undefined
      const key = hasExistingValue ? currentDefault !== undefined ? pathDefault : path : path
      response = set(cloneDeep(response), key, newValue)
      continue
    }

    const key = currentDefault !== undefined ? pathDefault : currentValue !== undefined ? path : pathDefault
    const value = currentDefault !== undefined ? currentDefault : currentValue !== undefined ? { default: currentValue } : null
    response = set(cloneDeep(response), key, value)
  }

  return response
}

const formatUnit = (value = '') => value
  .replace(/predefined|unitless|_|-(?!\d)/g, '')
  .replace(/(?<=\d)-/g, '')
  .replace(/\/(?!\d)/g, '')
  .replace(/(?<!\d)\//g, '')

const removeUnusedUnits = obj => {
  if (Array.isArray(obj)) {
    return obj.map(formatUnit)
  }

  if (typeof obj === 'object' && obj !== null) {
    const formatted = {}
    for (const key in obj) {
      if (hasKey(obj, key)) {
        formatted[key] = formatUnit(obj[key])
      }
    }
    return formatted
  }

  if (typeof obj === 'string') {
    return formatUnit(obj)
  }

  return obj
}

function parseTailwindValue(value, prefix = '', prefixArbitrary = prefix, forceArbitrary = false) {
  if (!value) return ''

  value = removeUnusedUnits(value)

  const classes = [
    {
      className: ({ value }) => {
        value = String(value)
        const isArbitrary = /(?=.*\d)(?=.*[a-zA-Z%])/.test(value) && !PREDEFINED_SIZES.some(item => value.includes(item)) || forceArbitrary
        const notArbitraryNegative = !isArbitrary && value.slice(0, 1) === '-'
        const notArbitraryValue = `${notArbitraryNegative ? '-' : ''}${prefix}${value.replace('-', '').split('.')[0]}`
        const arbitraryValue = `${prefixArbitrary}[${value}]`
        return isArbitrary ? arbitraryValue : notArbitraryValue
      }, condition: ({ value }) => value, value: { value },
    },
  ]

  return filterClasses(classes)
}

function parseFontSize(value) {
  if (!value) {
    return ''
  }

  return filterClasses([
    { className: ({ value }) => `text-${value}`, condition: ({ value }) => `${value}`.endsWith('xl'), value: { value } },
    { className: ({ value }) => parseTailwindValue(value, 'text-'), condition: ({ value }) => !`${value}`.endsWith('xl'), value: { value } },
  ])
}

export function parseSpacing(spacing) {
  if (!spacing) {
    return ''
  }

  const onlyUnits = UNIT_OPTIONS.map(item => item?.value)
  const validSpacing = Object.entries(spacing).filter(([_, value]) => !!value && !onlyUnits.includes(value))
  if (!validSpacing.length) {
    return ''
  }

  return validSpacing.map(([key, value]) => {
    const parsed = parseTailwindValue(value, `${key}-`)
    if (parsed === null) return null
    return parsed
  }).filter(Boolean).join(' ')
}

export function parseColor(color, prefix = '') {
  if (!color && isEmpty(color)) {
    return ''
  }

  const classes = [
    {
      className: ({ color }) => {
        const value = typeof color === 'string' ? color : color?.rgbaParsed || color?.hex
        const arbitrary = color?.rgbaParsed || color?.hex
        return parseDefault(value, prefix, arbitrary)
      }, condition: ({ color }) => !!color, value: { color },
    },
  ]

  return filterClasses(classes)
}

export function parseTextAlign(textAlign = {}, type = 'flex') {
  const { horizontal, vertical } = textAlign

  const horizontalParser = {
    left: { flex: 'justify-start', text: 'text-left' },
    center: { flex: 'justify-center', text: 'text-center' },
    right: { flex: 'justify-end', text: 'text-right' },
    justify: { flex: 'text-justify', text: 'text-justify' },
  }

  const verticalParser = {
    top: { flex: 'items-start', text: 'align-top' },
    middle: { flex: 'items-center', text: 'align-middle' },
    bottom: { flex: 'items-end', text: 'align-bottom' },
  }

  const classes = [
    { className: ({ horizontal }) => horizontalParser[horizontal][type], condition: ({ horizontal }) => !!horizontal, value: { horizontal } },
    { className: ({ vertical }) => verticalParser[vertical][type], condition: ({ vertical }) => !!vertical, value: { vertical } },
    { className: () => 'flex', condition: ({ vertical, horizontal }) => (!!vertical || !!horizontal) && type === 'flex', value: { vertical, horizontal } },
  ]

  return filterClasses(classes)
}

function parseFontDecoration(decoration, transform) {
  const parser = {
    underline: 'underline',
    overline: 'overline',
    strike: 'line-through',
    uppercase: 'uppercase',
    lowercase: 'lowercase',
    capitalize: 'capitalize',
  }

  const classes = [
    { className: ({ decoration }) => parser[decoration?.value || decoration], condition: ({ decoration }) => decoration && parser[decoration?.value || decoration], value: { decoration } },
    { className: ({ color }) => parseColor(color, 'decoration'), condition: ({ color }) => color, value: decoration },
    { className: ({ style }) => style, condition: ({ style }) => style, value: decoration },
    { className: ({ thickness }) => parseTailwindValue(thickness, 'decoration-'), condition: ({ thickness }) => thickness, value: decoration },
    { className: ({ underlineOffset }) => parseTailwindValue(underlineOffset, 'underline-offset-'), condition: ({ underlineOffset, value }) => underlineOffset && value === 'underline', value: decoration },
    { className: ({ transform }) => parser[transform], condition: ({ transform }) => transform && parser[transform], value: { transform } },
  ]

  return filterClasses(classes)
}

function parseGridRows(rows, height, heightFixed) {
  if (!rows) {
    return ''
  }

  const classes = [
    { className: ({ rows }) => `grid-rows-${rows}`, condition: ({ height }) => !height || height === 'fillcontainer', value: { rows, height } },
    { className: ({ rows }) => `grid-rows-[repeat(${rows},_min-content)]`, condition: ({ height }) => height === 'fitcontent', value: { rows, height } },
    { className: ({ rows, heightFixed }) => `grid-rows-[repeat(${rows},_${heightFixed})]`, condition: ({ height }) => height === 'fixed' && heightFixed },
  ]

  return filterClasses(classes)
}

function parseGridColumns(columns, width, widthFixed) {
  if (!columns) {
    return ''
  }

  const classes = [
    { className: ({ columns, widthFixed }) => `grid-cols-[repeat(${columns},_${widthFixed.replace('_', '')})]`, condition: ({ width }) => width === 'fixed' && widthFixed, value: { columns, width, widthFixed } },
    { className: ({ columns }) => `grid-cols-${columns}`, condition: ({ width }) => width !== 'fixed', value: { columns, width } },
  ]

  return filterClasses(classes)
}

function parseLayout({ layoutType, flex, grid }) {
  const classes = [
    { className: () => 'flex', condition: ({ layoutType, direction }) => layoutType === 'flex' || direction, value: { layoutType, ...flex } },
    { className: ({ direction, reverse }) => reverse ? `flex-${direction.split('-')[1]}-reverse` : direction, condition: ({ layoutType, direction }) => layoutType === 'flex' && direction, value: { layoutType, ...flex } },
    { className: ({ distribute }) => distribute, condition: ({ layoutType, distribute }) => layoutType === 'flex' || distribute, value: { layoutType, ...flex } },
    { className: ({ align }) => align, condition: ({ layoutType, align }) => layoutType === 'flex' || align, value: { layoutType, ...flex } },
    { className: ({ wrap }) => wrap, condition: ({ layoutType, wrap }) => layoutType === 'flex' || wrap, value: { layoutType, ...flex } },
    { className: ({ gap }) => parseTailwindValue(gap?.all || gap, 'gap-'), condition: ({ layoutType, gap }) => layoutType === 'flex' && gap && gap.all || typeof gap === 'string', value: { layoutType, ...flex } },
    { className: ({ gap }) => parseTailwindValue(gap.x, 'gap-x-'), condition: ({ layoutType, gap }) => layoutType === 'flex' && gap && gap?.x, value: { layoutType, ...flex } },
    { className: ({ gap }) => parseTailwindValue(gap.y, 'gap-y-'), condition: ({ layoutType, gap }) => layoutType === 'flex' && gap && gap?.y, value: { layoutType, ...flex } },
    { className: ({ grow }) => `grow-${grow}`, condition: ({ layoutType, grow }) => layoutType === 'flex' && grow, value: { layoutType, ...flex } },
    { className: ({ shrink }) => `shrink-${shrink}`, condition: ({ layoutType, shrink }) => layoutType === 'flex' && shrink, value: { layoutType, ...flex } },

    { className: () => 'grid', condition: ({ layoutType, columns }) => layoutType === 'grid' || columns, value: { layoutType, ...grid } },
    { className: ({ direction, dense }) => direction + (dense === 'yes' ? '-dense' : ''), condition: ({ layoutType, direction }) => layoutType === 'grid' && direction, value: { layoutType, ...grid } },
    { className: ({ columns, width, widthFixed }) => parseGridColumns(columns, width, widthFixed), condition: ({ layoutType, columns }) => layoutType === 'grid' || columns, value: { layoutType, ...grid } },
    { className: ({ rows, height, heightFixed }) => parseGridRows(rows, height, heightFixed), condition: ({ layoutType, rows }) => layoutType === 'grid' || rows, value: { layoutType, ...grid } },
    { className: ({ align }) => align, condition: ({ layoutType, align }) => layoutType === 'grid' || align, value: { layoutType, ...grid } },
    { className: ({ itemColumn }) => parseTailwindValue(itemColumn, 'col-span-'), condition: ({ layoutType, itemColumn }) => layoutType === 'grid' || itemColumn, value: { ...grid } },
    { className: ({ itemRow }) => parseTailwindValue(itemRow, 'row-span-'), condition: ({ layoutType, itemRow }) => layoutType === 'grid' || itemRow, value: { ...grid } },
    { className: ({ gap }) => parseTailwindValue(gap?.all || gap, 'gap-'), condition: ({ layoutType, gap }) => layoutType === 'grid' && gap && gap.all || typeof gap === 'string', value: { layoutType, ...grid } },
    { className: ({ gap, gapX }) => parseTailwindValue(gapX || gap.x, 'gap-x-'), condition: ({ layoutType, gap }) => layoutType === 'grid' && gap && gap?.x || gap?.gapX, value: { layoutType, ...grid } },
    { className: ({ gap, gapY }) => parseTailwindValue(gapY || gap.y, 'gap-y-'), condition: ({ layoutType, gap }) => layoutType === 'grid' && gap && gap?.y || gap?.gapY, value: { layoutType, ...grid } },
  ]

  return filterClasses(classes)
}

function parseOverflow({ overflow }) {
  if (!overflow) {
    return
  }

  const classes = [
    {
      className: ({ all, x, y, type }) => [
        all && type !== 'axis' ? `overflow-${all}` : '',
        type === 'axis' && x ? `overflow-x-${x}` : '',
        type === 'axis' && y ? `overflow-y-${y}` : '',
      ].filter(Boolean).join(' '),
      condition: ({ all, x, y, type = 'all' }) => all || (type === 'axis' && (x || y)),
      value: overflow,
    },
  ]

  return filterClasses(classes)
}

function parseDefault(value, prefix, arbitrary) {
  if (!value) {
    return ''
  }

  prefix = prefix ? `${prefix}-` : ''
  return `${prefix}${arbitrary ? `[${value}]` : value}`
}

function parseImage(image, includeUrl = true) {
  if (!image || !image.src) {
    return ''
  }

  const classes = [
    { className: ({ image }) => `bg-[url(${image.src})]`, condition: ({ includeUrl }) => includeUrl, value: { image, includeUrl } },
    { className: ({ image }) => image.attachment, condition: ({ image }) => image.attachment, value: { image } },
    { className: ({ image }) => image.position, condition: ({ image }) => image.position, value: { image } },
    { className: ({ image }) => `bg-${image.size}`, condition: ({ image }) => image.size, value: { image } },
    { className: ({ image }) => image.repeat, condition: ({ image }) => image.repeat, value: { image } },
  ]

  return filterClasses(classes)
}

export function parseGradient(gradientProp = {}) {
  if (!gradientProp?.colors?.length) {
    return ''
  }

  const gradient = cloneDeep(gradientProp)
  const { type = 'linear', colors, radial, direction } = gradient

  const getBackground = color => typeof color === 'string' ? `var(--color-${color})` : color?.rgbaParsed || color?.hex
  const linear = { ...gradient.linear || {}, direction: gradient?.linear?.direction || direction }
  const hasStop = stop => stop?.value !== undefined && stop?.value !== null && stop?.value !== ''

  const withStops = colors.map((stop, index) => hasStop(stop) ? stop : { ...stop, value: getGradientColorValue(colors.length, index) })
  const colorsWithValues = gradient?.direction ? colors.map((color, index) => ({ color, value: getGradientColorValue(gradient?.colors?.length, index) })) : withStops

  const gradientColors = colorsWithValues
    .sort((a, b) => a.value - b.value)
    .filter(({ color }) => typeof color === 'string' ? color : color?.hex)
    .map(stop => `${getBackground(stop.color)}_${stop.value}%`)
    .join(',')

  if (!gradientColors) {
    return ''
  }

  const gradientConfig = {
    linear: {
      class: 'linear-gradient',
      args: [linear.degrees && `${linear.degrees}deg`, linear.direction].filter(Boolean).join(''),
    },
    conic: {
      class: 'conic-gradient',
      args: '',
    },
    radial: {
      class: 'radial-gradient',
      args: [radial?.shape, radial?.size].filter(Boolean).join('_'),
    },
  }

  const config = gradientConfig[type]
  const gradientContent = [config.args, gradientColors].filter(Boolean).join(',')
  return `${config.class}(${gradientContent})`
}

function parseImageGradient(image, gradient) {
  if (!image || !gradient) {
    return ''
  }

  const gradientClasses = parseGradient(gradient)
  return `bg-[${gradientClasses},url(${image.src})] ${parseImage(image, false)} `
}

export function removeEmptyObjects(obj) {
  if (Array.isArray(obj)) return obj.map(removeEmptyObjects)
  if (typeof obj !== 'object' || obj === null) return obj

  const result = {}
  for (const key in obj) {
    if (hasKey(obj, key)) {
      const value = removeEmptyObjects(obj[key])
      const isEmptyValue = value === undefined || value === null
      const isEmptyObject = typeof value === 'object' && value !== null && Object.keys(value).length === 0
      if (!isEmptyValue && !isEmptyObject) {
        result[key] = value
      }
    }
  }

  const keepNullDefault = hasKey(obj, 'default') && obj.default === null && Object.keys(result).length > 0
  if (keepNullDefault) {
    result.default = null
  }

  return result
}

function isEmpty(value) {
  if ([null, undefined, ''].includes(value) || isEmptyLodash(value) || JSON.stringify(value) === '{}' || JSON.stringify(removeEmptyObjects(value)) === '{}') {
    return true
  }

  return false
}

function parseBackground(background) {
  if (!background) {
    return ''
  }

  const classes = [
    { className: ({ image, gradient }) => parseImageGradient(image, gradient), condition: ({ image, gradient }) => image && gradient, value: background },
    { className: ({ color }) => parseColor(color, 'bg'), condition: ({ image, gradient, color }) => color && (!image || !gradient), value: background },
    { className: ({ image }) => parseImage(image), condition: ({ image, gradient }) => image && !gradient, value: background },
    { className: ({ gradient }) => `bg-[${parseGradient(gradient)}]`, condition: ({ image, gradient }) => !image && parseGradient(gradient), value: background },
    { className: ({ blendMode }) => blendMode.value, condition: ({ blendMode }) => !!blendMode?.value, value: background },
    { className: ({ origin }) => origin.value, condition: ({ origin }) => !!origin?.value, value: background },
    { className: ({ clip }) => clip, condition: ({ clip }) => !!clip, value: background },
    { className: ({ mixBlendMode }) => mixBlendMode.value, condition: ({ mixBlendMode }) => !!mixBlendMode?.value, value: background },
  ]

  return filterClasses(classes)
}

function parseShadow(shadow, prefix) {
  if (!shadow) {
    return ''
  }

  const getValues = shadow => {
    const { value, x: xCraft = '', y: yCraft = '', blur: blurCraft = '', unit: craftUnit = 'px' } = shadow
    if (value !== 'arbitrary') {
      return value
    }

    return {
      x: xCraft && [xCraft, craftUnit].join('_'),
      y: yCraft && [yCraft.split('_')[0], craftUnit].join('_'),
      blur: blurCraft && [blurCraft.split('_')[0], craftUnit].join('_'),
    }
  }

  const shadowClass = shadow => {
    const value = getValues(shadow)

    const isArbitrary = typeof value !== 'string'

    if (isArbitrary && (!value?.x || !value?.y)) {
      return ''
    }

    const arbitraryClass = [
      value?.x && value?.x?.replace('_', ''),
      value?.y && value?.y?.replace('_', ''),
      value?.blur && value?.blur?.replace('_', ''),
    ].filter(Boolean).join('_')
    return isArbitrary ? `${prefix}-[${arbitraryClass}]` : [prefix, value].join('-')
  }

  const classes = [
    { className: ({ color }) => parseColor(color, prefix), condition: ({ color }) => !!color, value: shadow },
    { className: shadow => shadowClass(shadow), condition: shadow => shadow && shadow?.value, value: shadow },
  ]

  return filterClasses(classes)
}

function parseBorder(border = {}, prefix, name = prefix) {
  const classes = [
    { className: ({ color }) => parseColor(color, prefix), condition: ({ color }) => !!color, value: border },
    { className: ({ width }) => parseSpacing(width).replace(name, prefix), condition: ({ width }) => !!width, value: border },
    { className: ({ offset }) => parseSpacing(offset).replace(name, prefix), condition: ({ offset }) => !!offset && Object.values(offset).length, value: border },
    { className: ({ style }) => parseDefault(style, prefix), condition: ({ style }) => !!style, value: border },
  ]

  return filterClasses(classes)
}

export function hasKeyDeep(obj, targetKey) {
  if (typeof obj !== 'object' || obj === null) {
    return false
  }

  if (hasKey(obj, targetKey)) {
    return true
  }

  for (const key in obj) {
    if (typeof obj[key] === 'object') {
      if (hasKeyDeep(obj[key], targetKey)) {
        return true
      }
    }
  }

  return false
}

const addScreenPrefix = (parsedClass, mobileClass, alwaysAddPrefix, utilityPrefix) => {
  if (!mobileClass.includes(utilityPrefix)) {
    return false
  }

  if (alwaysAddPrefix) {
    return true
  }

  if (mobileClass.split(' ').some(item => parsedClass.includes(item.split('-')[0]))) {
    return true
  }

  return ADD_SCREEN_PREFIX.includes(parsedClass.split('-')[0]) && ADD_SCREEN_PREFIX.includes(mobileClass.split('-')[0])
}

function getClass({ value, condition, className, mobileClass = '' }, alwaysAddPrefix) {
  if (!value) {
    return ''
  }

  const utilitiesKeys = parseByUtility(value)

  return Object.entries(utilitiesKeys).map(([key, value]) => {
    if (isEmpty(value) || !condition(value) || !className(value)) {
      return ''
    }

    const classes = className(value).split(' ')
    const utilityPrefix = key === 'default' ? '' : `${key.replaceAll(':default', '')}:`
    const parsedClasses = classes.map(item => `${utilityPrefix}${item}`).join(' ')
    const mobileClassWithoutDuplicates = mobileClass.split(' ').filter(item => !parsedClasses.split(' ').includes(item)).join(' ')
    const screenPrefix = mobileClassWithoutDuplicates && addScreenPrefix(parsedClasses, mobileClass, alwaysAddPrefix, utilityPrefix) ? HTML_BREAKPOINT : ''

    return parsedClasses.split(' ').map(parsedClass => `${screenPrefix}${parsedClass}`).join(' ')
  }).filter(Boolean).join(' ')
}

function filterClasses(classes) {
  return classes.map(({ className, condition, value, alwaysAddPrefix }) => {
    if (isEmpty(value)) {
      return
    }

    const mobileValue = getDeviceValues(value, 'mobile')
    const desktopValue = getDeviceValues(value, 'desktop')

    const mobileClass = mobileValue && getClass({ value: mobileValue, condition, className })
    const desktopClass = desktopValue && getClass({ value: desktopValue, condition, className, mobileClass }, alwaysAddPrefix)
    return [desktopClass, mobileClass].filter(Boolean).join(' ')
  }).filter(Boolean).join(' ').trim()
}

export function parseByUtility(input, separator = ':') {
  const result = {}

  if (input?.default !== undefined) {
    return input
  }

  const allUtilitiesKeys = ['default', ...globalUtilitiesList]

  const walk = (obj, path = [], prefix = []) => {
    for (const [key, value] of Object.entries(obj)) {
      if (isPlainObject(value)) {
        const utilityKeys = Object.keys(value).filter(currentKey => allUtilitiesKeys.some(utilKey => currentKey === utilKey || currentKey.includes(utilKey)))

        if (utilityKeys.length > 0) {
          for (const utilKey of utilityKeys) {
            const utilValue = value[utilKey]
            const combinedKey = utilKey === 'default' ? prefix.join('-') || 'default' : [...prefix, utilKey].join(separator)

            if (isPlainObject(utilValue)) {
              walk({ [key]: utilValue }, path, [...prefix, utilKey])
            } else {
              set(result, [combinedKey, ...path, key], utilValue)
            }
          }
        } else {
          walk(value, [...path, key], prefix)
        }
      } else {
        const combinedKey = prefix.length ? prefix.join(separator) : 'default'
        set(result, [combinedKey, ...path, key], value)
      }
    }
  }

  walk(input)
  return result
}

export const hasKey = (value = {}, key) => Object.prototype.hasOwnProperty.call(value, key)

export const getDeviceValues = (value, device) => {
  if (device === 'mobile' && !hasKeyDeep(value, device)) {
    return ''
  }

  if (hasKey(value, device)) {
    return value[device]
  }

  const result = Object.entries(value).reduce((acc, [key, val]) => {
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      if (hasKey(val, device)) {
        const deviceVal = val[device]
        if (deviceVal !== undefined) {
          acc[key] = deviceVal
        }
      } else {
        const canNast = device === 'desktop' ? !hasKey(val, 'mobile') : true
        const nested = getDeviceValues(val, device) || {}
        if (Object.keys(nested).length && canNast) {
          acc[key] = nested
        } else if (device === 'desktop' && !hasKeyDeep(val, 'mobile')) {
          acc[key] = val
        }
      }
    } else if (val !== undefined && val !== null || key === 'default') {
      if (device === 'desktop' && !hasKeyDeep(val, 'mobile')) {
        acc[key] = val
      }
    }
    return acc
  }, {})

  return Object.keys(result).length > 0 ? result : null
}

const parsePosition = position => {
  if (!position) {
    return ''
  }

  const parseAllPositions = ({ top, right, bottom, left }) => {
    const positions = [
      { value: top, prefix: 'top-' },
      { value: right, prefix: 'right-' },
      { value: bottom, prefix: 'bottom-' },
      { value: left, prefix: 'left-' },
    ]

    return positions.map(({ value, prefix }) => parseTailwindValue(value, prefix)).filter(Boolean).join(' ')
  }

  return filterClasses([
    { alwaysAddPrefix: true, value: { position }, className: ({ position: { value } }) => value, condition: ({ position: { value } }) => value },
    { alwaysAddPrefix: true, value: { position }, className: ({ position }) => parseAllPositions(position), condition: ({ position: { value } }) => value && value !== 'static' },
  ])
}

export const parseTimingFunction = (value, map = EASE_MAP) => {
  if (typeof value === 'string') {
    return map[value] || value
  }

  return `cubic-bezier(${round(value[1].x, 3)},${round(value[1].y, 3)},${round(value[2].x, 3)},${round(value[2].y, 3)})`
}

const parseTransitionMultiple = transition => {
  transition = Array.isArray(transition) ? transition : [transition]
  if (!transition?.length) {
    return ''
  }

  const PREDEFINED_TRANSITIONS = {
    all: ['color', 'background-color', 'border-color', 'outline-color', 'text-decoration-color', 'fill', 'stroke', 'opacity', 'box-shadow', 'transform', 'translate', 'scale', 'rotate', 'filter', 'backdrop-filter', 'display', 'content-visibility', 'overlay', 'pointer-events'],
    colors: ['color', 'background-color', 'border-color', 'outline-color', 'text-decoration-color', 'fill', 'stroke'],
    opacity: ['opacity'],
    shadow: ['box-shadow'],
    transform: ['transform', 'translate', 'scale', 'rotate'],
  }

  const getProperty = transition => {
    const { type, property } = transition
    const propertyValue = property?.value || property
    if (type === 'arbitrary') {
      return propertyValue
    }

    return PREDEFINED_TRANSITIONS[propertyValue] || propertyValue
  }

  const parserItem = transition => {
    const { duration, delay, ease, behavior } = transition
    const property = getProperty(transition)

    if (!property) {
      return ''
    }

    const isMultiple = Array.isArray(property)
    if (isMultiple) {
      const items = []
      for (const item of property) {
        items.push(parserItem({ property: item, duration, delay, ease, behavior, type: 'arbitrary' }))
      }
      return items.join(',')
    }

    return [property, duration && `${duration}ms`, ease && parseTimingFunction(ease, EASE_MAP), delay && `${delay}ms`].filter(Boolean).join('_')
  }

  const parserWrapper = transitions => {
    const items = transitions.map(transition => parserItem(transition)).filter(Boolean).join(',')
    return items ? `[transition:${items}]` : ''
  }

  const classes = [
    { value: transition, className: transition => parserWrapper(transition), condition: transition => transition && transition?.length },
    { value: transition, className: transition => transition.find(item => item.behavior)?.behavior, condition: transition => transition && transition?.length && transition.some(item => item.behavior) },
  ]

  return filterClasses(classes)
}

const parseTransitionSingle = transition => {
  const classes = [
    { value: { transition }, className: ({ transition: { property, property: { value } } }) => Array.isArray(value || property) ? `transition-[${(value || property).join(',')}]` : `transition-${property}`, condition: ({ transition: { property } }) => property },
    { value: { transition }, className: ({ transition: { duration } }) => `duration-[${duration}ms]`, condition: ({ transition: { duration } }) => duration },
    { value: { transition }, className: ({ transition: { delay } }) => `delay-[${delay}ms]`, condition: ({ transition: { delay } }) => delay },
    { value: { transition }, className: ({ transition: { ease } }) => parseDefault(parseTimingFunction(ease, {}), typeof ease !== 'string' ? 'ease' : '', typeof ease !== 'string'), condition: ({ transition: { ease } }) => ease },
    { value: { transition }, className: ({ transition: { behavior } }) => behavior, condition: ({ transition: { behavior } }) => behavior },
  ]

  return filterClasses(classes)
}

const parseTransition = transition => {
  if (!transition || !transition?.length) {
    return ''
  }

  if (Array.isArray(transition) && transition?.length > 1) {
    return parseTransitionMultiple(transition)
  }

  transition = Array.isArray(transition) ? transition[0] : transition
  return parseTransitionSingle(transition)
}

const parseAnimation = animation => {
  if (!animation) {
    return ''
  }

  const animateClass = animation => {
    const { name, duration, delay, ease, loops, direction, fill } = animation
    const durationValue = duration ? `${duration}ms` : ''
    const delayValue = delay ? `${delay}ms` : ''
    const easeValue = ease && parseTimingFunction(ease, {})
    return name && durationValue ? [name, durationValue, easeValue, delayValue, loops, direction, fill].filter(Boolean).join('_') : ''
  }

  const animateWrapper = animation => {
    const animations = Array.isArray(animation) ? animation.map(item => animateClass(item)) : [animateClass(animation)]
    const animationsFiltered = animations.filter(Boolean)
    return animationsFiltered?.length ? `animate-[${animationsFiltered.join(',')}]` : ''
  }

  const classes = [
    {
      value: { animation },
      className: ({ animation }) => animateWrapper(animation),
      condition: ({ animation }) => !!animation,
    },
  ]

  return filterClasses(classes)
}

export const parseTransform = transforms => {
  if (!transforms || !Object.keys(transforms).length) {
    return ''
  }

  const transformClass = (transform = {}) => {
    const { name, type = 'all', value = '' } = transform

    const axisPrefix = type === 'all' ? '' : type.toLowerCase()
    const { defaultUnit = '', getAllClass } = TRANSFORM_CONFIG.find(item => item.value === name)?.config || {}
    const withoutUnit = value.split('/').length === 1
    const valueUnit = withoutUnit ? `${value}${defaultUnit}` : value.replace('/', '')
    return type === 'all' ? getAllClass(valueUnit) : `${name}${axisPrefix}(${valueUnit})`
  }

  const classes = [
    {
      value: { ...transforms },
      className: ({ defaultItems }) => `transform-[${defaultItems.map(transform => transformClass(transform)).join('_')}]`,
      condition: ({ defaultItems }) => defaultItems?.length,
    },
    { value: { ...transforms }, className: ({ backface }) => backface, condition: ({ backface }) => !!backface },
    { value: { ...transforms }, className: ({ perspective }) => parseTailwindValue(perspective, 'perspective-'), condition: ({ perspective }) => !!perspective },
    { value: { ...transforms }, className: ({ perspectiveOrigin }) => `perspective-origin-${perspectiveOrigin}`, condition: ({ perspectiveOrigin }) => !!perspectiveOrigin },
    { value: { ...transforms }, className: ({ transformOrigin }) => `origin-${transformOrigin}`, condition: ({ transformOrigin }) => !!transformOrigin },
    { value: { ...transforms }, className: ({ transformStyle }) => transformStyle, condition: ({ transformStyle }) => !!transformStyle },
  ]

  return filterClasses(classes)
}

export const parseHide = hide => {
  const classes = filterClasses([{ alwaysAddPrefix: true, value: { hide }, className: ({ hide }) => hide === 'hide' ? 'hidden' : hide === 'show' ? 'block' : hide, condition: ({ hide }) => !!hide }])
  return classes.split(' ').map(item => item.includes(HTML_BREAKPOINT) ? `${item}!` : item).join(' ')
}

export const parseFilters = filters => {
  if (!filters || !Object.keys(filters).length) {
    return ''
  }

  const getParsers = (value, key, backdrop = false) => {
    const parsers = {
      'hue-rotate': () => parseTailwindValue(`${value}deg`, `${backdrop ? 'backdrop-' : ''}${key}-`),
      'drop-shadow': () => parseShadow(value, 'drop-shadow'),
      default: () => parseTailwindValue(value, `${backdrop ? 'backdrop-' : ''}${key}-`),
    }

    return parsers[key] ? parsers[key]() : parsers.default()
  }

  const parseClasses = (filter, isBackdrop) => {
    const filterClass = Object.entries(filter).map(([key, value]) => getParsers(value, key, isBackdrop)).join(' ')
    return filterClass || ''
  }

  const classes = [
    { value: filters.filter, className: filter => parseClasses(filter), condition: filter => filter && Object.keys(filter).length },
    { value: filters.backdrop, className: backdrop => parseClasses(backdrop, true), condition: backdrop => backdrop && Object.keys(backdrop).length },
  ]

  return filterClasses(classes)
}

const parseCustomSize = size => {
  if (!size) {
    return ''
  }

  const classes = [
    { value: { size }, className: ({ size }) => size.width, condition: ({ size }) => size.width },
    { value: { size }, className: ({ size }) => size.height, condition: ({ size }) => size.height },
    { value: { size }, className: ({ size: { min } }) => min?.width.includes('w-') ? `min-w-${min?.width.replace('w-', '')}` : parseTailwindValue(min?.width, 'min-w-'), condition: ({ size: { min } }) => min?.width },
    { value: { size }, className: ({ size: { max } }) => max?.width.includes('w-') ? `max-w-${max?.width.replace('w-', '')}` : parseTailwindValue(max?.width, 'max-w-'), condition: ({ size: { max } }) => max?.width },
    { value: { size }, className: ({ size: { min } }) => min?.height.includes('h-') ? `min-h-${min?.height.replace('h-', '')}` : parseTailwindValue(min?.height, 'min-h-'), condition: ({ size: { min } }) => min?.height },
    { value: { size }, className: ({ size: { max } }) => max?.height.includes('h-') ? `max-h-${max?.height.replace('h-', '')}` : parseTailwindValue(max?.height, 'max-h-'), condition: ({ size: { max } }) => max?.height },
  ]

  return filterClasses(classes)
}

const parseOverscroll = overscroll => {
  if (!overscroll) {
    return ''
  }

  const classes = [
    {
      className: ({ value, x, y }) => [
        parseTailwindValue(value, 'overflow-'),
        parseTailwindValue(x, 'overflow-x-'),
        parseTailwindValue(y, 'overflow-y-'),
      ].filter(Boolean).join(' '),
      condition: overscroll => overscroll,
      value: overscroll,
    },
  ]

  return filterClasses(classes)
}

const parseScroll = scroll => {
  if (!scroll) {
    return ''
  }

  const parseAllPositions = ({ top, right, bottom, left }, prefix) => {
    const positions = [
      { value: top, prefix: `${prefix}t-` },
      { value: right, prefix: `${prefix}r-` },
      { value: bottom, prefix: `${prefix}b-` },
      { value: left, prefix: `${prefix}l-` },
    ]

    return positions.map(({ value, prefix }) => parseTailwindValue(value, prefix)).filter(Boolean).join(' ')
  }

  const classes = [
    { className: ({ behavior }) => parseDefault(behavior, 'scroll'), condition: ({ behavior }) => behavior, value: scroll },
    { className: ({ snap }) => parseDefault(snap?.align, 'snap'), condition: ({ snap }) => snap?.align, value: scroll },
    { className: ({ snap }) => parseDefault(snap?.stop, 'snap'), condition: ({ snap }) => snap?.stop, value: scroll },
    { className: ({ snap }) => parseDefault(snap?.type, 'snap'), condition: ({ snap }) => snap?.type, value: scroll },
    { className: ({ margin }) => parseAllPositions(margin, 'scroll-m'), condition: ({ margin }) => margin, value: scroll, alwaysAddPrefix: true },
    { className: ({ padding }) => parseAllPositions(padding, 'scroll-p'), condition: ({ padding }) => padding, value: scroll, alwaysAddPrefix: true },
  ]

  return filterClasses(classes)
}

const parseMask = mask => {
  if (!mask) {
    return ''
  }

  const parseAxis = (value, prefix) => {
    if (!value?.x && !value?.y && !value?.value) {
      return ''
    }

    const valuesParsed = [
      !value?.x ? null : /\d/.test(value.x) ? parseTailwindValue(value.x, '').replace('[', '').replace(']', '') : value?.x,
      !value?.y ? null : /\d/.test(value.y) ? parseTailwindValue(value.y, '').replace('[', '').replace(']', '') : value?.y,
      !value?.value ? null : /\d/.test(value.value) ? parseTailwindValue(value.value, '').replace('[', '').replace(']', '') : value?.value,
    ].filter(Boolean)
    const classesParsed = valuesParsed.join('_')
    const isArbitrary = valuesParsed.length > 1 || /\d/.test(classesParsed)
    return parseDefault(classesParsed, prefix, isArbitrary)
  }

  const parseProperties = properties => {
    const gradient = properties?.gradient && parseGradient(properties?.gradient)
    const image = properties?.image && `url(${properties.image.src})`
    const classes = [gradient, image].filter(Boolean).join(',')
    return `mask-[${classes}]`
  }

  const classes = [
    { className: ({ clip }) => parseDefault(clip, 'mask'), condition: ({ clip }) => clip, value: mask },
    { className: ({ composite }) => parseDefault(composite, 'mask'), condition: ({ composite }) => composite, value: mask },
    { className: ({ mode }) => parseDefault(mode, 'mask'), condition: ({ mode }) => mode, value: mask },
    { className: ({ origin }) => parseDefault(origin, 'mask-origin'), condition: ({ origin }) => origin, value: mask },
    { className: ({ type }) => parseDefault(type, 'mask-type'), condition: ({ type }) => type, value: mask },
    { className: ({ position }) => parseAxis(position, 'mask-position'), condition: ({ position }) => position, value: mask },
    { className: ({ size }) => parseAxis(size, 'mask-size'), condition: ({ size }) => size, value: mask },
    { className: ({ properties }) => parseProperties(properties), condition: ({ properties }) => properties, value: mask },
  ]

  return filterClasses(classes)
}

export function parseClassName(props) {
  const {
    className: defautlClassName,
    customClassName,
    opacity,
    hide,
    border,
    ring,
    outline,
    textAlign,
    textAlignContainer,
    textColor,
    lineHeight,
    letterSpacing,
    fontFamily,
    fontSerif,
    fontSize,
    fontWeight,
    decoration,
    transform,
    size = {},
    space = {},
    background,
    shadow,
    textShadow,
    image,
    position,
    transition = {},
    transforms = {},
    animation = {},
    cursor,
    nativeStyle,
    paragraphSpacing,
    groupPropagation,
    peerPropagation,
    contentUtility,
    filters,
    zindex,
    align,
    display,
    pointerEvents,
    spinnerLoading,
    overflowWrap,
    wordBreak,
    whitespace,
    textWrap,
    textIndent,
    textOverflow,
    lineClamp,
    variantNumeric,
    float,
    boxSizing,
    aspectRatio,
    overscroll,
    accentColor,
    resize,
    userSelect,
    fieldSizing,
    fill,
    stroke,
    willChange,
    clear,
    boxDecorationBreak,
    scroll,
    mask,
  } = props

  const className = [...new Set([
    filterClasses([
      { value: { opacity }, className: ({ opacity }) => `opacity-${opacity}`, condition: ({ opacity }) => !['', null, undefined].includes(opacity) },
      { value: { fontFamily, fontSerif }, className: ({ fontFamily, fontSerif }) => parseDefault(`'${fontFamily}'${fontSerif ? `,${fontSerif}` : ''}`, 'font', true), condition: ({ fontFamily }) => !!fontFamily },
      { value: { fontWeight }, className: ({ fontWeight }) => parseTailwindValue(fontWeight.replace('font-', ''), 'font-', 'font-', /\d/.test(fontWeight)), condition: ({ fontWeight }) => fontWeight },
      { value: { borderRadius: border?.radius }, className: ({ borderRadius }) => parseSpacing(borderRadius), condition: ({ borderRadius }) => !!borderRadius && Object.values(borderRadius).length },
      { value: { space }, className: ({ space }) => space.padding || '', condition: ({ space }) => space.padding },
      { value: { space }, className: ({ space }) => space.margin || '', condition: ({ space }) => space.margin },
      { value: { ...image }, className: ({ fit }) => fit, condition: ({ fit }) => !!fit },
      { value: { ...image }, className: ({ position }) => position, condition: ({ position }) => !!position },
      { value: { customClassName }, className: ({ customClassName }) => customClassName.join(' '), condition: ({ customClassName }) => customClassName && customClassName.length },
      { value: { defautlClassName }, className: ({ defautlClassName }) => [].concat(defautlClassName).join(' '), condition: ({ defautlClassName }) => defautlClassName && defautlClassName.length },
      { value: { cursor }, className: ({ cursor }) => cursor, condition: ({ cursor }) => cursor },
      { value: { nativeStyle }, className: ({ nativeStyle }) => nativeStyle.includes('appearance-') ? nativeStyle : `[appearance:${nativeStyle}]`, condition: ({ nativeStyle }) => nativeStyle, alwaysAddPrefix: true },
      { value: { paragraphSpacing }, className: ({ paragraphSpacing }) => `${parseTailwindValue(paragraphSpacing, '[&_p:not(:last-child)]:pb-')} ${parseTailwindValue(paragraphSpacing, '[&>p:not(:last-child)]:pb-')}`, condition: ({ paragraphSpacing }) => !!paragraphSpacing },
      { value: { contentUtility }, className: ({ contentUtility }) => `content-['${contentUtility.replaceAll(' ', '_')}']`, condition: ({ contentUtility }) => contentUtility },
      { value: { groupPropagation }, className: () => 'group', condition: ({ groupPropagation }) => groupPropagation },
      { value: { peerPropagation }, className: () => 'peer', condition: ({ peerPropagation }) => peerPropagation },
      { value: { letterSpacing }, className: ({ letterSpacing }) => parseTailwindValue(letterSpacing, 'tracking-'), condition: ({ letterSpacing }) => letterSpacing },
      { value: { lineHeight }, className: ({ lineHeight }) => parseTailwindValue(lineHeight, 'leading-'), condition: ({ lineHeight }) => lineHeight },
      { value: { zindex }, className: ({ zindex }) => parseTailwindValue(zindex, 'z-'), condition: ({ zindex }) => zindex },
      { value: { display }, className: ({ display }) => display, condition: ({ display }) => display, alwaysAddPrefix: true },
      { value: { pointerEvents }, className: ({ pointerEvents }) => pointerEvents, condition: ({ pointerEvents }) => pointerEvents },
      { value: { spinnerLoading }, className: ({ spinnerLoading }) => spinnerLoading ? 'after:hidden [&.loading]:after:block [&.loading>span]:hidden [&.loading]:h-10' : '', condition: ({ spinnerLoading }) => spinnerLoading },
      { value: { overflowWrap }, className: ({ overflowWrap }) => overflowWrap, condition: ({ overflowWrap }) => overflowWrap },
      { value: { wordBreak }, className: ({ wordBreak }) => wordBreak, condition: ({ wordBreak }) => wordBreak },
      { value: { whitespace }, className: ({ whitespace }) => whitespace, condition: ({ whitespace }) => whitespace },
      { value: { textWrap }, className: ({ textWrap }) => textWrap, condition: ({ textWrap }) => textWrap },
      { value: { textIndent }, className: ({ textIndent }) => parseTailwindValue(textIndent, 'indent-'), condition: ({ textIndent }) => textIndent },
      { value: { textOverflow }, className: ({ textOverflow }) => textOverflow, condition: ({ textOverflow }) => textOverflow },
      { value: { lineClamp }, className: ({ lineClamp }) => parseTailwindValue(lineClamp, 'line-clamp-'), condition: ({ lineClamp }) => lineClamp },
      { value: { variantNumeric }, className: ({ variantNumeric }) => variantNumeric.join(' '), condition: ({ variantNumeric }) => variantNumeric && variantNumeric.length },
      { value: { float }, className: ({ float }) => float, condition: ({ float }) => float },
      { value: { boxSizing }, className: ({ boxSizing }) => boxSizing, condition: ({ boxSizing }) => boxSizing },
      { value: { aspectRatio }, className: ({ aspectRatio }) => parseTailwindValue(aspectRatio, 'aspect-', 'aspect-', /\d/.test(aspectRatio)), condition: ({ aspectRatio }) => aspectRatio },
      { value: { accentColor }, className: ({ accentColor }) => parseColor(accentColor, 'accent'), condition: ({ accentColor }) => accentColor },
      { value: { fill }, className: ({ fill }) => parseColor(fill, 'fill'), condition: ({ fill }) => fill },
      { value: { resize }, className: ({ resize }) => resize, condition: ({ resize }) => resize },
      { value: { userSelect }, className: ({ userSelect }) => userSelect, condition: ({ userSelect }) => userSelect },
      { value: { fieldSizing }, className: ({ fieldSizing }) => fieldSizing, condition: ({ fieldSizing }) => fieldSizing },
      { value: { willChange }, className: ({ willChange }) => `will-change-[${willChange.join(',')}]`, condition: ({ willChange }) => willChange && willChange.length },
      { value: { clear }, className: ({ clear }) => clear, condition: ({ clear }) => clear },
      { value: { boxDecorationBreak }, className: ({ boxDecorationBreak }) => boxDecorationBreak, condition: ({ boxDecorationBreak }) => boxDecorationBreak },
    ]),
    parseFontSize(fontSize),
    parseTextAlign(align, 'flex'),
    parseTextAlign(align, 'text'),
    parseTextAlign(textAlign, 'flex'),
    parseTextAlign(textAlignContainer, 'text'),
    parseColor(textColor, 'text'),
    parseFontDecoration(decoration, transform),
    parseLayout(props),
    parseOverflow(props),
    parseBackground(background),
    parseShadow(shadow, 'shadow'),
    parseShadow(textShadow, 'text-shadow'),
    parseBorder(border, 'border'),
    parseBorder(ring, 'ring'),
    parseBorder(outline, 'outline'),
    parseBorder(stroke, 'stroke'),
    parsePosition(position),
    parseTransition(transition),
    parseAnimation(animation),
    parseTransform(transforms),
    parseHide(hide),
    parseFilters(filters),
    parseCustomSize(size),
    parseOverscroll(overscroll),
    parseScroll(scroll),
    parseMask(mask),
  ].filter(Boolean).join(' ').split(' '))]

  const removeDuplicates = className.filter(item => !item.startsWith(HTML_BREAKPOINT) || !className.includes(item.replace(HTML_BREAKPOINT, '')))
  return removeDuplicates.join(' ')
}

export function useHTMLClassnames(props, customUtilities = []) {
  updateGlobalUtilities(customUtilities)

  const propsMemo = useRef({ props: null, customUtilities: null, className: null })
  const propsValue = propsMemo.current

  if (!isEqual(propsValue.props, props) || !isEqual(propsValue.customUtilities, customUtilities)) {
    propsValue.props = props
    propsValue.customUtilities = customUtilities
    propsValue.className = parseClassName(props)
  }

  useCraftProp('attributes[className]', propsValue.className)
  return propsValue.className
}

const ADD_SCREEN_PREFIX = [
  'm', 'my', 'mx', 'mt', 'mb', 'ml', 'mr',
  'p', 'py', 'px', 'pt', 'pb', 'pl', 'pr',
  'static', 'relative', 'absolute', 'fixed', 'sticky',
  'appearance',
]

const PREDEFINED_SIZES = ['xs', 'sm', 'md', 'lg', 'xl']

const EASE_MAP = {
  'ease-linear': 'linear',
  'ease-in': 'var(--ease-in)',
  'ease-out': 'var(--ease-out)',
  'ease-in-out': 'var(--ease-in-out)',
  'ease-initial': 'initial',
}
